// Everyday edits to a customer record (office-history prototype): their details, a new contact, a
// phone's or email's consent, an address's name and access notes, equipment at an address, tags,
// a note on a job and an office file on a job. Each form is plain text; these turn it into the
// action's input, sending only what changed, or say the one thing to fix first.

import { normalizePhone } from '@flowology/kernel';
import type { Address, ContactMethod, Customer, Equipment } from '../types.ts';

/**
 * What the person may change here, by the permissions they hold (each action's own, packages/auth
 * permissions.ts; QA-681), whichever role gives it. The server checks every write again, so a control
 * left out by mistake is only a missing button, never a hole.
 * While the company mirrors ServiceTitan (`mirroring`), the customer, their contacts, addresses,
 * equipment and tags, a job's reopening and a merge are ServiceTitan's (st-mirror §2): off then. A
 * note, a job note, a file, the billing terms and a portal link's revoke stay Flowology's own.
 */
export function editRights(held: readonly string[], mirroring = false) {
  const has = (p: string) => held.includes(p);
  const ours = !mirroring;
  return {
    /** customers.edit: details, contacts, consent, addresses, equipment, tags on the record */
    edit: ours && has('customers.edit'),
    /** A note on the customer or an address (customers.addNote, records.addNote: customers.edit) */
    note: has('customers.edit'),
    /** portalLinks.revoke (customers.edit): a customer's link stopped (Flowology's own) */
    revoke: has('customers.edit'),
    /** An address's access notes and codes and its site contact (customers.edit): Flowology's own, while mirroring too */
    place: has('customers.edit'),
    /** customers.pii.view: access codes are shown and edited only by these */
    accessCodes: has('customers.pii.view'),
    /** org.edit: a new tag for the whole company */
    createTag: has('org.edit'),
    /** notes.add on any job needs jobs.dispatch (a CSR holds notes.add, but only for jobs they're on) */
    jobNote: has('notes.add') && has('jobs.dispatch'),
    /** media.add: an office file on any job (the office's: with jobs.dispatch, as a job note) */
    files: has('media.add') && has('jobs.dispatch'),
    /** jobs.reopen: a completed job back for a warranty callback, as Review's rights.reopen (F-31) */
    reopen: ours && has('jobs.reopen'),
    /** receivables.manage: days to pay and the PO rule are money (F-29) */
    terms: has('receivables.manage'),
    /** customers.taxExempt: tax exemption (BL-094), the owner's and the bookkeeper's; ServiceTitan's while mirroring */
    taxExempt: ours && has('customers.taxExempt'),
    /** customers.merge: a duplicate merged into another, everything moving (BL-029) */
    merge: ours && has('customers.merge'),
    /** ServiceTitan owns the record while the company mirrors it: say so where the controls were. */
    mirroring,
  };
}

type Built = { input: Record<string, unknown> } | { error: string };
const NOTHING = 'Nothing has changed yet.';

// ── details (customers.update) ──

/** An address as typed: every part a string, empty for none. */
export interface AddressForm {
  line1: string;
  line2: string;
  city: string;
  region: string;
  postal: string;
}
export const addressForm = (a: Partial<Address> | null | undefined): AddressForm => ({
  line1: a?.line1 ?? '',
  line2: a?.line2 ?? '',
  city: a?.city ?? '',
  region: a?.region ?? '',
  postal: a?.postal ?? '',
});
const blankAddress = (f: AddressForm) => !Object.values(f).some((v) => v.trim());

export interface DetailsForm {
  displayName: string;
  kind: Customer['kind'];
  status: Customer['status'];
  doNotContact: boolean;
  /** Only when customers.get sends them (the notes and billing address are newer reads). */
  notes: string;
  billing: AddressForm;
}
export const detailsForm = (c: Customer): DetailsForm => ({
  displayName: c.displayName,
  kind: c.kind,
  status: c.status,
  doNotContact: c.doNotContact,
  notes: c.notes ?? '',
  billing: addressForm(c.billingAddress),
});
export function detailsInput(c: Customer, f: DetailsForm): Built {
  const name = f.displayName.trim();
  if (!name) return { error: 'Add their name.' };
  const changed: Record<string, unknown> = {};
  if (name !== c.displayName) changed.displayName = name;
  if (f.kind !== c.kind) changed.kind = f.kind;
  if (f.status !== c.status) changed.status = f.status;
  if (f.doNotContact !== c.doNotContact) changed.doNotContact = f.doNotContact;
  if (c.notes !== undefined && (f.notes.trim() || null) !== c.notes) changed.notes = f.notes.trim() || null;
  if (c.billingAddress !== undefined) {
    const was = addressForm(c.billingAddress);
    if (JSON.stringify(f.billing) !== JSON.stringify(was)) {
      // A billing address is sent whole (customers.update replaces it); emptied, it goes back to the service address.
      if (blankAddress(f.billing)) changed.billingAddress = null;
      else {
        const b = f.billing;
        if (!b.line1.trim() || !b.city.trim())
          return { error: 'A billing address needs its street and city.' };
        const country = c.billingAddress?.country ?? c.locations[0]?.country ?? 'CA';
        changed.billingAddress = {
          line1: b.line1.trim(),
          ...(b.line2.trim() ? { line2: b.line2.trim() } : {}),
          city: b.city.trim(),
          ...(b.region.trim() ? { region: b.region.trim() } : {}),
          ...(b.postal.trim() ? { postal: b.postal.trim().toUpperCase() } : {}),
          country,
        };
      }
    }
  }
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: c.id, ...(c.rowVersion ? { rowVersion: c.rowVersion } : {}), ...changed } };
}

// ── a new contact (customers.addContact) ──

/** Where consent came from, as the office can record it (an import or a STOP text is the system's). */
export const CONSENT_SOURCES = [
  ['verbal', 'They said so (call or in person)'],
  ['written', 'In writing'],
  ['customer_request', 'They asked us'],
  ['web_form', 'Web form'],
  ['portal', 'Customer portal'],
] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number][0];

export interface ContactForm {
  name: string;
  title: string;
  phone: string;
  sms: boolean;
  email: string;
  mail: boolean;
  source: ConsentSource;
}
export const emptyContactForm = (): ContactForm => ({
  name: '',
  title: '',
  phone: '',
  sms: false,
  email: '',
  mail: false,
  source: 'verbal',
});
export function contactInput(customerId: string, f: ContactForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Add their name.' };
  const phone = f.phone.trim() ? normalizePhone(f.phone) : undefined;
  if (phone === null)
    return { error: 'That phone number doesn’t look right. Type it with its area code, like 604-555-0198.' };
  const email = f.email.trim();
  const title = f.title.trim();
  return {
    input: {
      customerId,
      contact: {
        name,
        ...(title ? { title } : {}),
        phones: phone ? [{ value: phone, smsConsent: f.sms, consentSource: f.source }] : [],
        emails: email ? [{ value: email, emailConsent: f.mail, consentSource: f.source }] : [],
      },
    },
  };
}

// ── a contact's phones and emails (customers.addContactMethod, updateContactMethod; the CRM walk) ──

const BAD_PHONE = 'That phone number doesn’t look right. Type it with its area code, like 604-555-0198.';
const BAD_EMAIL = 'That email doesn’t look right. Check it has an @ and a domain, like name@example.com.';
/** A phone as E.164, or an email trimmed and lowercased; null when it can't be one. */
function methodValue(kind: 'phone' | 'email', typed: string): string | null {
  const v = typed.trim();
  if (kind === 'phone') return normalizePhone(v);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v.toLowerCase() : null;
}

export interface MethodForm {
  kind: 'phone' | 'email';
  value: string;
  /** They agreed to service texts (a phone) or emails (an email) on it, now. */
  consent: boolean;
  source: ConsentSource;
}
export const emptyMethodForm = (): MethodForm => ({
  kind: 'phone',
  value: '',
  consent: false,
  source: 'verbal',
});
export function methodInput(contactId: string, f: MethodForm): Built {
  if (!f.value.trim()) return { error: f.kind === 'phone' ? 'Type the number.' : 'Type the email.' };
  const value = methodValue(f.kind, f.value);
  if (!value) return { error: f.kind === 'phone' ? BAD_PHONE : BAD_EMAIL };
  return {
    input: {
      contactId,
      method:
        f.kind === 'phone'
          ? { kind: 'phone', value, smsConsent: f.consent, consentSource: f.source }
          : { kind: 'email', value, emailConsent: f.consent, consentSource: f.source },
    },
  };
}
/** A phone or email corrected: only its value; the server clears the old one's consent. */
export function changeMethodInput(m: Pick<ContactMethod, 'id' | 'kind' | 'value'>, typed: string): Built {
  const value = methodValue(m.kind, typed);
  if (!value) return { error: m.kind === 'phone' ? BAD_PHONE : BAD_EMAIL };
  if (value === m.value) return { error: NOTHING };
  return { input: { methodId: m.id, value } };
}

// ── one phone's or email's consent (customers.setConsent) ──

export interface ConsentForm {
  consent: boolean;
  marketing: boolean;
  doNotContact: boolean;
  reason: string;
  source: ConsentSource;
}
export const consentForm = (m: ContactMethod): ConsentForm => ({
  consent: m.consent,
  marketing: m.marketingConsent,
  doNotContact: m.doNotContact,
  reason: '',
  source: 'verbal',
});
export function consentInput(m: ContactMethod, f: ConsentForm): Built {
  // Marketing rides on service consent: without it, marketing goes too (the server does the same).
  const marketing = f.consent && f.marketing;
  const changed: Record<string, unknown> = {};
  if (f.consent !== m.consent) changed.consent = f.consent;
  if (marketing !== m.marketingConsent) changed.marketingConsent = marketing;
  if (f.doNotContact !== m.doNotContact) changed.doNotContact = f.doNotContact;
  if (!Object.keys(changed).length) return { error: NOTHING };
  const reason = f.reason.trim();
  return {
    input: {
      contactMethodId: m.id,
      source: f.source,
      ...changed,
      ...(f.doNotContact && changed.doNotContact !== undefined && reason
        ? { doNotContactReason: reason }
        : {}),
    },
  };
}
/** What a phone or email agreed to, in words. */
export function consentWords(m: ContactMethod, customerDnc: boolean): string {
  if (customerDnc || m.doNotContact) return 'Do not contact';
  const what = m.kind === 'phone' ? 'Texts' : 'Emails';
  if (!m.consent) return `No ${what.toLowerCase()}`;
  return m.marketingConsent ? `${what} OK, offers too` : `${what} OK`;
}

// ── an address (customers.updateLocation) ──
// The street is edited only once customers.get sends the whole address (line 2, region, postal):
// updateLocation merges the parts it's given, and an empty string clears one.

type Loc = Customer['locations'][number];
/** Whether this location came with its whole address, so its street can be changed safely. */
export const hasWholeAddress = (l: Loc) =>
  l.line2 !== undefined && l.region !== undefined && l.postal !== undefined;
export interface LocationForm {
  name: string;
  accessNotes: string;
  accessCodes: string;
  street: AddressForm;
  /** The site contact's id (F-54), '' for nobody, or SOMEONE_ELSE for one added here. */
  siteContactId: string;
  /** "Someone else…": a person only on this address, a tenant or a caretaker. */
  someone: { name: string; phone: string; sms: boolean; source: ConsentSource };
}
/** The site contact picker's "Someone else…". */
export const SOMEONE_ELSE = 'someone-else';
const noOne = () => ({ name: '', phone: '', sms: false, source: 'verbal' as ConsentSource });
export const locationForm = (l: Loc): LocationForm => ({
  name: l.name ?? '',
  accessNotes: l.accessNotes ?? '',
  accessCodes: l.accessCodes ?? '',
  street: addressForm(l),
  siteContactId: l.siteContact?.id ?? '',
  someone: noOne(),
});

/** Who may be the site contact (F-54): the customer's contacts, those only here, and the bill-to's. */
export function siteContactChoices(
  c: Customer,
  l: Loc,
  billTo: Customer | null,
): { label: string; people: { id: string; name: string }[] }[] {
  const named = (ks: { id: string; name: string; title: string | null }[]) =>
    ks.map((k) => ({ id: k.id, name: k.title ? `${k.name} (${k.title})` : k.name }));
  return [
    { label: c.displayName, people: named(c.contacts) },
    { label: 'Only at this address', people: named(l.contacts ?? []) },
    ...(billTo ? [{ label: `${billTo.displayName} (bills to)`, people: named(billTo.contacts) }] : []),
  ].filter((g) => g.people.length);
}

/** customers.addContact for "Someone else…": on this address only, with the id it gets. */
export function someoneInput(l: Loc, contactId: string, f: LocationForm['someone']): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Add the site contact’s name.' };
  const phone = f.phone.trim() ? normalizePhone(f.phone) : undefined;
  if (phone === null) return { error: BAD_PHONE };
  if (!phone) return { error: 'Add the site contact’s phone: visit texts go to it.' };
  return {
    input: {
      locationId: l.id,
      contactId,
      contact: { name, phones: [{ value: phone, smsConsent: f.sms, consentSource: f.source }] },
    },
  };
}

export function locationInput(
  l: Loc,
  f: LocationForm,
  withCodes: boolean,
  /** The id "Someone else…" was added under. */
  someoneId?: string,
): Built {
  const val = (s: string) => s.trim() || null;
  const changed: Record<string, unknown> = {};
  if (val(f.name) !== (l.name ?? null)) changed.name = val(f.name);
  if (val(f.accessNotes) !== (l.accessNotes ?? null)) changed.accessNotes = val(f.accessNotes);
  if (withCodes && val(f.accessCodes) !== (l.accessCodes ?? null)) changed.accessCodes = val(f.accessCodes);
  const site = f.siteContactId === SOMEONE_ELSE ? (someoneId ?? '') : f.siteContactId;
  if (site !== (l.siteContact?.id ?? '')) changed.siteContactId = site || null;
  if (hasWholeAddress(l)) {
    const was = addressForm(l);
    const parts: Record<string, string> = {};
    for (const k of ['line1', 'line2', 'city', 'region', 'postal'] as const) {
      const now = k === 'postal' ? f.street[k].trim().toUpperCase() : f.street[k].trim();
      if (now !== was[k]) parts[k] = now; // '' clears line 2, region or postal
    }
    if (parts.line1 === '' || parts.city === '') return { error: 'An address needs its street and city.' };
    if (Object.keys(parts).length) changed.address = parts;
  }
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { id: l.id, ...(l.rowVersion ? { rowVersion: l.rowVersion } : {}), ...changed } };
}

/**
 * While the company mirrors ServiceTitan, only an address's own columns change (st-mirror §3.1): its
 * access notes and codes (customers.setAccess) and its site contact (customers.setSiteContact), one
 * input each for what changed. The address and its name are ServiceTitan's then; so is a new contact.
 */
export function ownLocationInputs(
  l: Loc,
  f: LocationForm,
  withCodes: boolean,
): { access?: Record<string, unknown>; site?: Record<string, unknown> } | { error: string } {
  const val = (s: string) => s.trim() || null;
  const access: Record<string, unknown> = {};
  if (val(f.accessNotes) !== (l.accessNotes ?? null)) access.accessNotes = val(f.accessNotes);
  if (withCodes && val(f.accessCodes) !== (l.accessCodes ?? null)) access.accessCodes = val(f.accessCodes);
  const siteChanged = f.siteContactId !== (l.siteContact?.id ?? '');
  if (!Object.keys(access).length && !siteChanged) return { error: NOTHING };
  // the row's version guards the first write; the second follows it in the same breath
  const version = l.rowVersion ? { rowVersion: l.rowVersion } : {};
  return {
    ...(Object.keys(access).length ? { access: { locationId: l.id, ...version, ...access } } : {}),
    ...(siteChanged
      ? {
          site: {
            locationId: l.id,
            ...(Object.keys(access).length ? {} : version),
            siteContactId: f.siteContactId || null,
          },
        }
      : {}),
  };
}

// ── another address (customers.addLocation) ──

export const emptyLocationForm = (): LocationForm => ({
  name: '',
  accessNotes: '',
  accessCodes: '',
  street: addressForm(null),
  siteContactId: '',
  someone: noOne(),
});
/** A second property, a rental, a strata's other building: in the customer's own country. */
export function newLocationInput(c: Customer, f: LocationForm, withCodes: boolean): Built {
  const s = f.street;
  if (!s.line1.trim() || !s.city.trim()) return { error: 'An address needs its street and city.' };
  const opt = (k: string, v: string) => (v.trim() ? { [k]: v.trim() } : {});
  return {
    input: {
      customerId: c.id,
      location: {
        address: {
          line1: s.line1.trim(),
          ...opt('line2', s.line2),
          city: s.city.trim(),
          ...opt('region', s.region),
          ...opt('postal', s.postal.toUpperCase()),
          country: c.locations[0]?.country ?? 'CA',
        },
        ...opt('name', f.name),
        ...opt('accessNotes', f.accessNotes),
        ...(withCodes ? opt('accessCodes', f.accessCodes) : {}),
      },
    },
  };
}

// ── a contact's name and role (customers.updateContact) ──

export function renameInput(
  c: { id: string; name: string; title: string | null; rowVersion?: number },
  name: string,
  title: string,
): Built {
  const n = name.trim();
  if (!n) return { error: 'Add their name.' };
  const changed: Record<string, unknown> = {};
  if (n !== c.name) changed.name = n;
  if (title.trim() !== (c.title ?? '')) changed.title = title.trim(); // '' clears it
  if (!Object.keys(changed).length) return { error: NOTHING };
  // the version it was read at: a change made since (another tab, QA-697) is refused, not overwritten
  return { input: { contactId: c.id, ...(c.rowVersion ? { rowVersion: c.rowVersion } : {}), ...changed } };
}

/** Who agreed to messages on this channel, when and how, in words; null when the server didn't say. */
export function consentLine(m: ContactMethod, dateText: (iso: string) => string): string | null {
  if (!m.consentAt) return null;
  const how =
    CONSENT_SOURCES.find((s) => s[0] === m.consentSource)?.[1] ?? SOURCE_OTHER[m.consentSource ?? ''] ?? null;
  const what = m.consent ? 'Agreed' : 'Withdrawn';
  return `${what} ${dateText(m.consentAt)}${m.consentBy ? `, recorded by ${m.consentBy}` : ''}${how ? ` · ${how.toLowerCase()}` : ''}`;
}
const SOURCE_OTHER: Record<string, string> = {
  import: 'From the import',
  sms_keyword: 'By text (STOP or START)',
};

// ── equipment at an address (equipment.add) ──

export interface EquipmentForm {
  kind: string;
  make: string;
  model: string;
  serial: string;
  size: string;
  installedOn: string;
  notes: string;
}
export const emptyEquipmentForm = (): EquipmentForm => ({
  kind: '',
  make: '',
  model: '',
  serial: '',
  size: '',
  installedOn: '',
  notes: '',
});
export function equipmentInput(locationId: string, f: EquipmentForm, today: string): Built {
  const kind = f.kind.trim();
  if (!kind) return { error: 'Say what it is, like “Water heater”.' };
  if (f.installedOn && f.installedOn > today) return { error: 'The install date can’t be in the future.' };
  const opt = (k: Exclude<keyof EquipmentForm, 'kind' | 'installedOn'>) =>
    f[k].trim() ? { [k]: f[k].trim() } : {};
  return {
    input: {
      locationId,
      kind,
      ...opt('make'),
      ...opt('model'),
      ...opt('serial'),
      ...opt('size'),
      ...(f.installedOn ? { installedOn: f.installedOn } : {}),
      ...opt('notes'),
    },
  };
}

/** An existing unit as the sheet edits it. */
export const equipmentForm = (e: Equipment): EquipmentForm => ({
  kind: e.kind,
  make: e.make ?? '',
  model: e.model ?? '',
  serial: e.serial ?? '',
  size: e.size ?? '',
  installedOn: e.installedOn ?? '',
  notes: e.notes ?? '',
});
/** equipment.update: only what changed (a blank clears it), with the version seen. */
export function equipmentChangeInput(e: Equipment, f: EquipmentForm, today: string): Built {
  const kind = f.kind.trim();
  if (!kind) return { error: 'Say what it is, like “Water heater”.' };
  if (f.installedOn && f.installedOn > today) return { error: 'The install date can’t be in the future.' };
  const was = equipmentForm(e);
  const changed: Record<string, unknown> = {};
  if (kind !== was.kind) changed.kind = kind;
  for (const k of ['make', 'model', 'serial', 'size', 'notes'] as const)
    if (f[k].trim() !== was[k].trim()) changed[k] = f[k].trim() || null;
  if (f.installedOn !== was.installedOn) changed.installedOn = f.installedOn || null;
  if (!Object.keys(changed).length) return { error: NOTHING };
  return { input: { equipmentId: e.id, ...changed, rowVersion: e.rowVersion } };
}
/** "Rheem XE40M · 40 gal · installed Jun 2019 · serial RH-0042": what the record says about a unit. */
export function equipmentLine(
  e: Pick<Equipment, 'make' | 'model' | 'size' | 'installedOn' | 'serial'>,
): string {
  const made = [e.make, e.model].filter(Boolean).join(' ');
  const installed = e.installedOn
    ? `installed ${new Intl.DateTimeFormat('en-CA', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${e.installedOn}T12:00:00Z`))}`
    : '';
  return [made, e.size ?? '', installed, e.serial ? `serial ${e.serial}` : ''].filter(Boolean).join(' · ');
}
export function retireInput(equipmentId: string, reason: string): Built {
  const r = reason.trim();
  if (!r) return { error: 'Say why it’s retired, like “Replaced” or “Removed”.' };
  return { input: { equipmentId, reason: r } };
}

// ── a note on a job (notes.add) ──

/** The note's id is the save key: sending the same note again after a lost answer adds it once. */
export function noteInput(jobId: string, body: string, noteId: string): Built {
  const b = body.trim();
  if (!b) return { error: 'Write the note first.' };
  return { input: { noteId, jobId, body: b } };
}

// ── a note on the customer themselves (customers.addNote, customers.notes) ──

/** One note from customers.notes: on the customer, or on one of their jobs (with its number). */
export interface CustomerNote {
  id: string;
  kind: 'customer' | 'job';
  jobId: string | null;
  jobNumber: string | null;
  body: string;
  authorName: string | null;
  occurredAt: string;
}

/** Like a job's note, the note's id is the save key, so a retry after a lost answer adds it once. */
export function customerNoteInput(customerId: string, body: string, noteId: string): Built {
  const b = body.trim();
  if (!b) return { error: 'Write the note first.' };
  return { input: { noteId, customerId, body: b } };
}

/**
 * Under a note: who wrote it and when, in the company's own time zone, so notes from one day can
 * be told apart: "Jamie Chen · 2:41 p.m." today, "Jamie Chen · Yesterday, 4:10 p.m.", then the day,
 * "Jamie Chen · Oct 14" (with the year only when it isn't this one).
 */
export function noteByline(
  n: Pick<CustomerNote, 'authorName' | 'occurredAt'>,
  timeZone: string,
  now = new Date(),
): string {
  const at = new Date(n.occurredAt);
  // ICU puts a narrow no-break space before "p.m."; the page shows a plain one
  const fmt = (o: Intl.DateTimeFormatOptions, d: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, ...o }).format(d).replace(/\s/g, ' ');
  const dayOf = (d: Date) => fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }, d);
  const today = dayOf(now);
  const [y = 0, m = 1, d = 1] = today.split('-').map(Number);
  const yesterday = new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
  const time = fmt({ hour: 'numeric', minute: '2-digit' }, at);
  const when =
    dayOf(at) === today
      ? time
      : dayOf(at) === yesterday
        ? `Yesterday, ${time}`
        : fmt(
            {
              month: 'short',
              day: 'numeric',
              ...(dayOf(at).slice(0, 4) === today.slice(0, 4) ? {} : { year: 'numeric' }),
            },
            at,
          );
  // the server names whoever wrote it (a staff record, else their sign-in name); null is what's left
  return `${n.authorName ?? 'Name not recorded'} · ${when}`;
}

// ── an office file on a job (attachments.add, then the upload) ──

/** What the server accepts (packages/modules media CONTENT_TYPES and MAX_BYTES). */
const PHOTO = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
const MAX = { photo: 25_000_000, video: 500_000_000, document: 50_000_000 } as const;
export const FILE_ACCEPT = [...PHOTO, 'application/pdf', 'video/mp4', 'video/quicktime', 'video/webm'].join(
  ',',
);
export const FILE_CATEGORIES = [
  ['document', 'Document'],
  ['receipt', 'Receipt'],
  ['before', 'Before'],
  ['after', 'After'],
  ['problem', 'Problem'],
  ['equipment', 'Equipment'],
  ['other', 'Other'],
] as const;

/** The kind a picked file is filed as, or why it can't be. */
export function fileKind(
  contentType: string,
  bytes: number,
): { kind: 'photo' | 'video' | 'document' } | { error: string } {
  const kind = PHOTO.includes(contentType)
    ? 'photo'
    : contentType.startsWith('video/') && ['video/mp4', 'video/quicktime', 'video/webm'].includes(contentType)
      ? 'video'
      : contentType === 'application/pdf'
        ? 'document'
        : null;
  if (!kind) return { error: 'That kind of file can’t be added. Use a photo, a PDF or a video.' };
  if (bytes > MAX[kind]) return { error: 'That file is too big.' };
  return { kind };
}

// ── a refusal, in the server's words ──

/** No answer from the server (offline, timed out, or it failed inside): the change may have gone in. */
export function unanswered(e: unknown): boolean {
  const r = e as { name?: string; code?: string };
  return r?.name !== 'RpcError' || r.code === 'offline' || r.code === 'internal';
}

/**
 * What to show when a write is refused: the server's message, or, for a form the server found
 * invalid, the field's own sentence when it has one ("That phone number doesn’t exist…"). A lost
 * answer gets a plain retry line: the held idempotency key makes trying again safe.
 */
export function refusal(e: unknown): string {
  const r = e as {
    name?: string;
    code?: string;
    message?: string;
    detail?: { issues?: { path?: (string | number)[]; message?: string }[] };
  };
  if (unanswered(e)) return 'Couldn’t save. Try again; it won’t be saved twice.';
  const issues = r.detail?.issues ?? [];
  const sentence = issues.find((i) => /[.!?]$/.test(i.message ?? ''))?.message;
  if (sentence) return sentence;
  // else the server's message, naming the field it found wrong ("… not valid. Check the email.")
  const path = (issues[0]?.path ?? []).filter((p): p is string => typeof p === 'string');
  const field = path.includes('emails') ? 'emails' : path.includes('phones') ? 'phones' : path.pop();
  const words = field && FIELD_WORDS[field];
  return `${r.message ?? 'Couldn’t save.'}${words ? ` Check the ${words}.` : ''}`;
}

/** The fields these forms send, in the words on the form. */
const FIELD_WORDS: Record<string, string> = {
  displayName: 'name',
  name: 'name',
  title: 'role',
  emails: 'email',
  phones: 'phone',
  accessNotes: 'access notes',
  accessCodes: 'access codes',
  kind: 'kind of equipment',
  installedOn: 'install date',
  body: 'note',
  caption: 'caption',
};

// ── a completed job reopened for a warranty callback (jobs.reopen), as Review does it (F-31) ──

/** The reason is required: it is the job's record of why it came back. */
export function reopenInput(jobId: string, reason: string): Built {
  const r = reason.trim();
  if (!r) return { error: 'Say why it’s being reopened.' };
  return { input: { jobId, reason: r, warranty: true } };
}

// ── billing terms (customers.setBillingTerms, F-29) ──

/** The days to pay the sheet offers; anything else is typed. */
export const TERMS_DAYS = [0, 15, 30, 45, 60] as const;
export interface TermsForm {
  /** '0'…'60', or 'other' with `days` typed */
  pick: string;
  days: string;
  poRequired: boolean;
}
export function termsForm(t: { poRequired: boolean; termsDays: number | null } | undefined): TermsForm {
  const d = t?.termsDays ?? 0;
  const listed = (TERMS_DAYS as readonly number[]).includes(d);
  return { pick: listed ? String(d) : 'other', days: listed ? '' : String(d), poRequired: !!t?.poRequired };
}
export function termsInput(
  customerId: string,
  was: { poRequired: boolean; termsDays: number | null } | undefined,
  f: TermsForm,
): Built {
  const raw = f.pick === 'other' ? f.days.trim() : f.pick;
  const days = Number(raw);
  if (!/^\d+$/.test(raw) || days > 365) return { error: 'Days to pay is a whole number, 0 to 365.' };
  const termsDays = days === 0 ? null : days;
  if (was && was.poRequired === f.poRequired && (was.termsDays || null) === termsDays)
    return { error: NOTHING };
  return { input: { customerId, poRequired: f.poRequired, termsDays } };
}

// ── tax exemption (customers.setTaxExempt, BL-094) ──

export interface TaxExemptForm {
  exempt: boolean;
  reference: string;
  reason: string;
}
export function taxExemptForm(
  t: { exempt: boolean; reference: string | null; reason: string | null } | undefined,
): TaxExemptForm {
  return { exempt: !!t?.exempt, reference: t?.reference ?? '', reason: t?.reason ?? '' };
}
export function taxExemptInput(
  customerId: string,
  was: { exempt: boolean; reference: string | null; reason: string | null } | undefined,
  f: TaxExemptForm,
): Built {
  const reference = f.exempt ? f.reference.trim() || null : null;
  const reason = f.exempt ? f.reason.trim() || null : null;
  if (reference && reference.length > 100) return { error: 'The reference is 100 characters at most.' };
  if (reason && reason.length > 200) return { error: 'The reason is 200 characters at most.' };
  if (
    was &&
    was.exempt === f.exempt &&
    (was.reference ?? null) === reference &&
    (was.reason ?? null) === reason
  )
    return { error: NOTHING };
  return { input: { customerId, taxExempt: f.exempt, reference, reason } };
}

/** What the record says of their tax: the words their documents carry, or that they're taxed. */
export function taxExemptText(t: {
  exempt: boolean;
  reference: string | null;
  reason: string | null;
}): string {
  if (!t.exempt) return 'Taxed as usual';
  const what = [t.reason, t.reference ? `(${t.reference})` : null].filter(Boolean).join(' ');
  return what ? `Tax exempt: ${what}` : 'Tax exempt';
}

// ── a job note corrected or removed (notes.update, notes.delete), and the notes' earlier words ──

/** The corrected words, with the version seen, so someone else's change in between is refused. */
export function noteEditInput(note: { id: string; rowVersion: number }, was: string, body: string): Built {
  const b = body.trim();
  if (!b) return { error: 'A note needs words. To take it off the job, remove it.' };
  if (b === was.trim()) return { error: NOTHING };
  return { input: { noteId: note.id, body: b, rowVersion: note.rowVersion } };
}

/** customers.notesHistory: the office notes' versions (kept from `since`), and job notes' changes. */
export interface NotesHistory {
  since: string;
  office: { before: string | null; after: string | null; by: string | null; at: string }[];
  jobNotes: {
    noteId: string;
    jobId: string;
    jobNumber: string;
    change: 'edited' | 'removed';
    before: string;
    after: string | null;
    by: string | null;
    at: string;
  }[];
}

/** "Changed by Dana Office" / "Removed by …": a line of the notes' history, without its time. */
export function historyLine(
  h: { by: string | null } & (
    | { kind: 'office'; before: string | null }
    | { kind: 'job'; change: 'edited' | 'removed'; jobNumber: string }
  ),
): string {
  const who = h.by ?? 'someone';
  if (h.kind === 'office') return h.before === null ? `Written by ${who}` : `Changed by ${who}`;
  return `${h.change === 'removed' ? 'Removed' : 'Corrected'} on job #${h.jobNumber} by ${who}`;
}
